import { NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Params, RouterLink } from '@angular/router';

import { Maybe } from '../../types/base';
import { Icon, icons } from '../../types/icon-list.type';
import { IconComponent } from '../icon/icon.component';
import { HasSelectedSignal, SELECTED_STATE_INJECTION_TOKEN } from '../menu/menu-trigger-state';
import type {
  ButtonFrame,
  ButtonIconPosition,
  ButtonNativeType,
  ButtonRel,
  ButtonSize,
  ButtonTarget,
  ButtonType,
} from './button.types';
import {
  buttonFrames,
  buttonIconPositions,
  buttonRels,
  buttonSizes,
  buttonTargets,
  buttonTypes,
} from './button.types';

@Component({
  selector: 'evc-button',
  imports: [IconComponent, NgTemplateOutlet, RouterLink],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-icon-only]': 'isIconOnly()',
    '[attr.data-full-width]': 'fullWidth()',
  },
  providers: [{ provide: SELECTED_STATE_INJECTION_TOKEN, useExisting: ButtonComponent }],
})
export class ButtonComponent implements HasSelectedSignal {
  size = input<Maybe<ButtonSize>>(buttonSizes.REGULAR);
  type = input<Maybe<ButtonType>>(undefined);
  nativeType = input<ButtonNativeType>();
  frame = input<Maybe<ButtonFrame>>(undefined);

  /** text content for the button - or use default content slot */
  text = input<string>();
  /** accessibility aria-label (e.g icon only buttons) */
  label = input<string>();

  /** if href - use <a> element */
  href = input<string>();
  /** if routerLink - use <a> with angular router */
  routerLink = input<string | string[]>();
  queryParams = input<Params>();
  /** if rel=external => default target blank + external icon */
  rel = input<ButtonRel>();
  target = input<ButtonTarget>();
  /** - <true> download the ressource on click.
   *  - <string> will rename the downloaded file
   */
  download = input<Maybe<true | string>>(undefined);

  icon = input<Icon>();
  iconPath = input<string>();
  iconPosition = input<Maybe<ButtonIconPosition>>(undefined);

  active = input(false, { transform: booleanAttribute });
  disabled = input(false, { transform: booleanAttribute });
  fullWidth = input(false, { transform: booleanAttribute });

  /** set by evcMenuTriggerState when used as a menu trigger */
  selectedState = signal(false);
  protected readonly isActive = computed(() => this.active() || this.selectedState());

  /** special defaults when rel[external] for easy links */
  protected readonly computedStyles = computed(() => {
    let type = this.type();
    let frame = this.frame();

    if (this.rel() === buttonRels.EXTERNAL) {
      type = type ?? buttonTypes.PRIMARY;
      frame = frame ?? buttonFrames.NONE;
    }
    return {
      type: type ?? buttonTypes.NEUTRAL,
      frame: frame ?? buttonFrames.FULL,
    };
  });

  /** special defaults when rel[external] for easy links */
  protected readonly computedIcon = computed(() => {
    let name = this.icon();
    let position = this.iconPosition();
    const path = this.iconPath();

    if (this.rel() === buttonRels.EXTERNAL) {
      name = name ?? (path ? undefined : icons.EXTERNAL_CONTEXT);
      position = position ?? buttonIconPositions.RIGHT;
    }
    return {
      name,
      position,
      path,
      exists: !!(name || path),
    };
  });

  // 👇 Detect if only icon so apply smaller paddings
  protected readonly projectedContent = viewChild<ElementRef<HTMLElement>>('projectedContent');
  readonly #projectedText = signal('');
  protected readonly isIconOnly = computed(
    () => this.computedIcon().exists && !this.text()?.trim() && !this.#projectedText(),
  );
  readonly #detectIfIconOnly = effect((onCleanup) => {
    const host = this.projectedContent()?.nativeElement;
    if (!host) {
      this.#projectedText.set('');
      return;
    }

    const updateProjectedText = () => this.#projectedText.set((host.textContent ?? '').trim());
    updateProjectedText();

    const observer = new MutationObserver(updateProjectedText);
    observer.observe(host, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    onCleanup(() => observer.disconnect());
  });

  protected readonly buttonRels = buttonRels;
  protected readonly buttonTargets = buttonTargets;
}
